import { ReactNode, useEffect, useRef, useState } from "react";
import { ChevronRight } from "react-bootstrap-icons";

const CLOSE_DELAY_MS = 150;

interface NavbarMenuGroupProps {
  title: string;
  children: ReactNode;
}

// Hover-triggered flyout for grouping related navbar items. The submenu is
// positioned with `fixed` (coordinates computed from the trigger's rect)
// rather than `absolute`, since the scrollable `.navbar__menu` ancestor would
// otherwise clip anything that opens outside its bounds.
const NavbarMenuGroup = ({ title, children }: NavbarMenuGroupProps) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
  };

  const openSubmenu = () => {
    clearCloseTimer();
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setCoords({ top: rect.top, left: rect.right + 8 });
    }
    setOpen(true);
  };

  // A short delay (rather than closing immediately on mouseleave) stops the
  // submenu from vanishing while the cursor is briefly outside both elements
  // as it travels across the gap towards the flyout.
  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => {
      setOpen(false);
    }, CLOSE_DELAY_MS);
  };

  useEffect(() => {
    return clearCloseTimer;
  }, []);

  return (
    <div
      className="navbar__menu-group"
      ref={triggerRef}
      onMouseEnter={openSubmenu}
      onMouseLeave={scheduleClose}
      onFocus={openSubmenu}
      onBlur={scheduleClose}
    >
      <button
        type="button"
        className="navbar__menu-item navbar__menu-group-toggle"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span>{title}</span>
        <ChevronRight size={12} aria-hidden="true" />
      </button>
      {open && (
        <div
          className="navbar__submenu"
          style={{ top: coords.top, left: coords.left }}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleClose}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default NavbarMenuGroup;
